import {Component, OnDestroy} from '@angular/core';
import {AuthenticationService} from '../user/authentication.service';
import {Tree} from '../model/tree.model';
import {Field} from '../model/field.model';
import {Line} from '../model/line.model';
import {StateService} from '../state.service';
import {User} from '../model/user.model';
import {Bonus} from '../model/bonus.model';
import {ActivatedRoute, Router} from '@angular/router';
import {TakeTruffle} from '../model/takeTruffle.model';
import {Subscription} from 'rxjs';
import {TruffleFound} from '../model/truffelFound.model';

interface TreeSummary {
  field?: Field;
  line?: Line;
  tree?: Tree;
  totalWeight: number;
  averageWeight: number;
  incomeBonus: number;
  sum: number;
}

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.css'],
    standalone: false
})
export class HomeComponent implements OnDestroy {
  fields: Field[] = [];
  trees: Tree[] = [];
  lines: Line[] = [];
  totalWeightPerTrees: number[] = [];
  avgTrufflePerTrees: number[] = [];
  incomeBonuses: number[] = [];
  sum: number[] = [];
  user?: User;
  totalSum = 0;
  isAdmin = false;

  private routeSubscription: Subscription;
  private userSubscription?: Subscription;
  private fieldSubscriptions: Subscription[] = [];
  private treeDataMap = new Map<string, TreeSummary>();
  private currentReferences: string[] = [];

  constructor(
      public authenticationService: AuthenticationService,
      public stateService: StateService,
      private route: ActivatedRoute,
      private router: Router
  ) {
    this.routeSubscription = this.route.paramMap.subscribe(params => {
      this.userSubscription?.unsubscribe();
      const userId = params.get('userId');

      if (userId) {
        this.isAdmin = true;
        this.userSubscription = this.stateService.getUser(userId).subscribe(user => {
          if (user) {
            this.user = user;
            this.takeData(user);
          } else {
            this.user = undefined;
            this.clearFieldData();
            this.recalculateTotalSum();
          }
        });
      } else {
        this.isAdmin = false;
        this.userSubscription = this.authenticationService.user$.subscribe(currentUser => {
          if (currentUser) {
            this.user = currentUser;
            this.takeData(currentUser);
          } else {
            this.user = undefined;
            this.clearFieldData();
            this.recalculateTotalSum();
          }
        });
      }
    });
  }

  ngOnDestroy(): void {
    this.routeSubscription.unsubscribe();
    this.userSubscription?.unsubscribe();
    this.clearFieldData();
  }

  private takeData(user: User): void {
    this.clearFieldData();
    this.treeDataMap.clear();
    this.currentReferences = user.treeReferences ? [...user.treeReferences] : [];

    if (!user.treeReferences || user.treeReferences.length === 0) {
      this.updateFromMap();
      return;
    }

    user.treeReferences.forEach(reference => {
      const [fieldId, lineId, treeId] = reference.split(':');
      const fieldSubscription = this.stateService.getField(fieldId).subscribe(field => {
        if (!field) {
          this.treeDataMap.set(reference, {
            totalWeight: 0,
            averageWeight: 0,
            incomeBonus: 0,
            sum: 0
          });
          this.updateFromMap();
          return;
        }

        const numericLineId = Number(lineId);
        const numericTreeId = treeId ? Number(treeId) : undefined;
        const line = field.lines.find(l => l.id === numericLineId);
        const startDate = this.getStartDateForReference(user, reference);

        if (!line) {
          const averageWeight = this.calculateAverageForField(field, startDate);
          const bonuses = this.getFieldBonusesTotal(field.id);
          this.treeDataMap.set(reference, {
            field,
            totalWeight: 0,
            averageWeight,
            incomeBonus: 0,
            sum: averageWeight + bonuses
          });
          this.updateFromMap();
          return;
        }

        const selectedTree =
          numericTreeId && !Number.isNaN(numericTreeId) && line.trees[numericTreeId - 1]
            ? line.trees[numericTreeId - 1]
            : line.trees[0];

        if (!selectedTree) {
          const averageWeight = this.calculateAverageForField(field, startDate);
          const bonuses = this.getFieldBonusesTotal(field.id);
          this.treeDataMap.set(reference, {
            field,
            line,
            totalWeight: 0,
            averageWeight,
            incomeBonus: 0,
            sum: averageWeight + bonuses
          });
          this.updateFromMap();
          return;
        }

        const averageWeight = this.calculateAverageForField(field, startDate);
        const treeForDisplay = this.getTreeWithFilteredTruffles(selectedTree, startDate);
        const totalWeight = this.calculateTotalWeight(treeForDisplay);
        const incomeBonus = this.calculateIncomeBonus(averageWeight, totalWeight);
        const bonuses = this.getFieldBonusesTotal(field.id);
        const sum = averageWeight + (averageWeight * incomeBonus / 100) + bonuses;

        this.treeDataMap.set(reference, {
          field,
          line,
          tree: treeForDisplay,
          totalWeight,
          averageWeight,
          incomeBonus,
          sum
        });
        this.updateFromMap();
      });

      this.fieldSubscriptions.push(fieldSubscription);
    });
  }

  private updateFromMap(): void {
    const newFields: Field[] = [];
    const newLines: Line[] = [];
    const newTrees: Tree[] = [];
    const newTotalWeights: number[] = [];
    const newAverages: number[] = [];
    const newBonuses: number[] = [];
    const newSums: number[] = [];

    this.currentReferences.forEach(reference => {
      const summary = this.treeDataMap.get(reference);
      if (!summary || !summary.field || !summary.line || !summary.tree) {
        return;
      }

      newFields.push(summary.field);
      newLines.push(summary.line);
      newTrees.push(summary.tree);
      newTotalWeights.push(summary.totalWeight);
      newAverages.push(summary.averageWeight);
      newBonuses.push(summary.incomeBonus);
      newSums.push(summary.sum);
    });

    this.fields = newFields;
    this.lines = newLines;
    this.trees = newTrees;
    this.totalWeightPerTrees = newTotalWeights;
    this.avgTrufflePerTrees = newAverages;
    this.incomeBonuses = newBonuses;
    this.sum = newSums;

    this.recalculateTotalSum();
  }

  private clearFieldData(): void {
    this.fieldSubscriptions.forEach(subscription => subscription.unsubscribe());
    this.fieldSubscriptions = [];
    this.treeDataMap.clear();
    this.currentReferences = [];
    this.fields = [];
    this.lines = [];
    this.trees = [];
    this.totalWeightPerTrees = [];
    this.avgTrufflePerTrees = [];
    this.incomeBonuses = [];
    this.sum = [];
    this.totalSum = 0;
  }

  private calculateTotalWeight(tree: Tree): number {
    if (!tree.truffleFounds || tree.truffleFounds.length === 0) {
      return 0;
    }
    return tree.truffleFounds.reduce((total, found) => total + (found.weight ?? 0), 0);
  }

  private calculateAverageForField(field: Field, startDate?: Date): number {
    if (!field.lines || field.lines.length === 0) {
      return 0;
    }
    let totalTrees = 0;
    let totalWeight = 0;
    const sinceDate = startDate;
    field.lines.forEach(line => {
      totalTrees += line.trees.length;
      line.trees.forEach(tree => {
        const filtered = this.filterTrufflesSince(tree.truffleFounds, sinceDate);
        filtered.forEach(found => {
          totalWeight += found.weight ?? 0;
        });
      });
    });
    return totalTrees ? totalWeight / totalTrees : 0;
  }

  private isAfterSponsoring(foundAt: string | undefined, since: Date): boolean {
    if (!foundAt) {
      return true;
    }
    const foundDate = new Date(foundAt);
    if (Number.isNaN(foundDate.getTime())) {
      return true;
    }
    return foundDate.getTime() >= since.getTime();
  }

  private filterTrufflesSince(founds: TruffleFound[] | undefined, startDate?: Date): TruffleFound[] {
    if (!founds || founds.length === 0) {
      return [];
    }
    if (!startDate) {
      return founds;
    }
    return founds.filter(found => this.isAfterSponsoring(found.foundAt, startDate));
  }

  private getTreeWithFilteredTruffles(tree: Tree, startDate?: Date): Tree {
    if (!tree) {
      return tree;
    }
    const founds = this.filterTrufflesSince(tree.truffleFounds, startDate);
    if (tree.truffleFounds === founds) {
      return tree;
    }
    return {
      ...tree,
      truffleFounds: founds
    };
  }

  private getStartDateForReference(user: User, reference: string): Date | undefined {
    const dateStr = user.treeReferenceDates?.[reference] ?? user.sponsoringSince;
    if (!dateStr) {
      return undefined;
    }
    const parsed = new Date(dateStr);
    if (Number.isNaN(parsed.getTime())) {
      return undefined;
    }
    parsed.setHours(0, 0, 0, 0);
    return parsed;
  }

  private calculateIncomeBonus(average: number, total: number): number {
    if (average > total) {
      return -10;
    }
    if (average < total) {
      return 10;
    }
    return 0;
  }

  private recalculateTotalSum(): void {
    const totalFromTrees = this.sum.reduce((acc, value) => acc + value, 0);
    const taken = this.user?.takeTruffle?.reduce((acc, item) => acc + item.weight, 0) ?? 0;
    this.totalSum = totalFromTrees - taken;
  }

  getFieldBonuses(fieldId: string): Bonus[] {
    if (!this.user?.sponsoringSince) {
      return [];
    }
    const bonuses: Bonus[] = [];
    this.stateService.bonuses.forEach(bonus => {
      if (bonus.fieldId === fieldId && new Date(this.user!.sponsoringSince) <= new Date(bonus.createdAt)) {
        bonuses.push(bonus);
      }
    });
    return bonuses;
  }

  getFieldBonusesTotal(fieldId: string): number {
    if (!this.user?.sponsoringSince) {
      return 0;
    }
    let total = 0;
    this.stateService.bonuses.forEach(bonus => {
      if (bonus.fieldId === fieldId && new Date(this.user!.sponsoringSince) <= new Date(bonus.createdAt)) {
        total += bonus.weight;
      }
    });
    return total;
  }

  takeTruffle() {
    if (!this.user) {
      return;
    }
    this.stateService.user = this.user;
    this.router.navigate(['/takeTruffle']);
  }

  deleteTakeTruffle(taken: TakeTruffle) {
    if (!this.user?.takeTruffle) {
      return;
    }
    const index: number = this.user.takeTruffle.indexOf(taken);
    if (index === -1) {
      return;
    }
    this.user.takeTruffle.splice(index, 1);
    this.stateService.editUser(this.user);
    this.recalculateTotalSum();
  }

  goToTruffle(tree: Tree) {
    this.stateService.tree = tree;
    this.router.navigate(['truffleFound']);
  }

  getTreeStartDate(index: number): Date | undefined {
    if (!this.user || index < 0 || index >= this.currentReferences.length) {
      return undefined;
    }
    const reference = this.currentReferences[index];
    const date = this.getStartDateForReference(this.user, reference);
    return date;
  }
}
